import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  IconButton,
  Collapse,
  Avatar,
  AvatarGroup,
  Tooltip,
} from '@mui/material';
import {
  Engineering as EngineeringIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Gavel as ComplianceIcon,
  Build as BuildIcon,
  SmartToy as AgentIcon,
  Build as ToolIcon,
} from '@mui/icons-material';
import { SpecializedProduct, SpecializedProductSubscription } from '../../types/billing';

interface SpecializedProductCardProps {
  product: SpecializedProduct;
  subscription?: SpecializedProductSubscription;
  onAddProduct: (productId: string, seatCount: number) => void;
  onUpdateSeats: (productId: string, seatCount: number) => void;
  onRemoveProduct: (productId: string) => void;
  onManageUsers?: (productId: string) => void;
  isLoading?: boolean;
  canAddProduct?: boolean;
}

export const SpecializedProductCard: React.FC<SpecializedProductCardProps> = ({
  product,
  subscription,
  onAddProduct,
  onUpdateSeats,
  onRemoveProduct,
  onManageUsers,
  isLoading,
  canAddProduct = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [seatCount, setSeatCount] = useState(subscription?.seatCount || product.minimumSeats);

  const isSubscribed = !!subscription;
  const monthlyTotal = seatCount * product.price.perUserMonthly;

  const handleSeatChange = (delta: number) => {
    const newCount = Math.max(
      product.minimumSeats,
      Math.min(seatCount + delta, product.maximumSeats || 999)
    );
    setSeatCount(newCount);
  };

  const getCategoryIcon = () => {
    switch (product.category) {
      case 'engineering':
        return <EngineeringIcon />;
      default:
        return <BuildIcon />;
    }
  };

  return (
    <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent style={{ flexGrow: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <Avatar style={{ backgroundColor: '#325BA8', marginRight: 16 }}>
            {getCategoryIcon()}
          </Avatar>
          <div style={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {product.name}
            </Typography>
            <Chip
              label={product.category.toUpperCase()}
              size="small"
              style={{ marginTop: 4 }}
            />
          </div>
        </div>

        <Typography variant="body2" color="text.secondary" paragraph>
          {product.description}
        </Typography>

        <div style={{ marginBottom: 16 }}>
          <Typography variant="h4" component="span" fontWeight="bold">
            ${product.price.perUserMonthly}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary">
            /user/month
          </Typography>
          <Typography variant="body2" color="text.secondary" style={{ marginTop: 4 }}>
            ${product.price.perUserAnnual}/user/year (save ${(product.price.perUserMonthly * 12 - product.price.perUserAnnual).toFixed(2)})
          </Typography>
        </div>

        {isSubscribed && subscription && (
          <div style={{ marginBottom: 16, padding: 16, backgroundColor: 'rgba(50,91,168,0.12)', borderRadius: 4 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Current Subscription
            </Typography>
            <Typography variant="body2">
              {subscription.seatCount} seat{subscription.seatCount > 1 ? 's' : ''} • ${(subscription.seatCount * subscription.pricePerSeat).toFixed(2)}/month
            </Typography>
            {subscription.assignedUsers && subscription.assignedUsers.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}>
                <AvatarGroup max={4} style={{ marginRight: 8 }}>
                  {subscription.assignedUsers.map((user) => (
                    <Tooltip key={user.id} title={user.userName}>
                      <Avatar style={{ width: 24, height: 24 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
                <Typography variant="caption">
                  {subscription.assignedUsers.length} assigned
                </Typography>
              </div>
            )}
          </div>
        )}

        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          style={{ marginBottom: 16 }}
        >
          {expanded ? 'Hide' : 'Show'} Features
        </Button>

        <Collapse in={expanded}>
          <Divider style={{ marginBottom: 16 }} />

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Included Teammates ({product.includedAgents.length})
          </Typography>
          <List dense style={{ marginBottom: 16 }}>
            {product.includedAgents.map((agent) => (
              <ListItem key={agent} style={{ paddingTop: 0, paddingBottom: 0 }}>
                <ListItemIcon style={{ minWidth: 32 }}>
                  <AgentIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={agent}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Specialized Tools
          </Typography>
          <List dense style={{ marginBottom: 16 }}>
            {product.specializedTools.map((tool) => (
              <ListItem key={tool} style={{ paddingTop: 0, paddingBottom: 0 }}>
                <ListItemIcon style={{ minWidth: 32 }}>
                  <ToolIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={tool}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Industry Compliance
          </Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {product.industryCompliance.map((compliance) => (
              <Chip
                key={compliance}
                label={compliance}
                size="small"
                icon={<ComplianceIcon />}
                variant="outlined"
              />
            ))}
          </div>

          {product.includesUnlimitedAgents && (
            <div style={{ marginTop: 16, padding: 8, backgroundColor: 'rgba(46,125,50,0.12)', borderRadius: 4 }}>
              <Typography variant="body2" fontWeight="bold">
                ✓ Includes unlimited custom teammate creation
              </Typography>
            </div>
          )}
        </Collapse>
      </CardContent>

      <CardActions style={{ padding: 16, paddingTop: 0, flexDirection: 'column', gap: 8 }}>
        {!isSubscribed ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
              <IconButton
                size="small"
                onClick={() => handleSeatChange(-1)}
                disabled={seatCount <= product.minimumSeats}
              >
                <RemoveIcon />
              </IconButton>
              <TextField
                size="small"
                type="number"
                value={seatCount}
                onChange={(e) => setSeatCount(Math.max(product.minimumSeats, parseInt(e.target.value) || 1))}
                style={{ width: 80 }}
                inputProps={{
                  min: product.minimumSeats,
                  max: product.maximumSeats,
                  style: { textAlign: 'center' }
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleSeatChange(1)}
                disabled={product.maximumSeats ? seatCount >= product.maximumSeats : false}
              >
                <AddIcon />
              </IconButton>
              <Typography variant="body2">
                seats
              </Typography>
            </div>
            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 8 }}>
              Total: ${monthlyTotal}/month
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => onAddProduct(product.id, seatCount)}
              disabled={isLoading || !canAddProduct}
            >
              Add to Subscription
            </Button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => onManageUsers?.(product.id)}
                disabled={isLoading}
                style={{ flex: 1 }}
              >
                Manage Users
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => onRemoveProduct(product.id)}
                disabled={isLoading}
                style={{ flex: 1 }}
              >
                Remove
              </Button>
            </div>
            {subscription.seatCount !== seatCount && (
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={() => onUpdateSeats(product.id, seatCount)}
                disabled={isLoading}
                style={{ marginTop: 8 }}
              >
                Update to {seatCount} seats
              </Button>
            )}
          </>
        )}
      </CardActions>
    </Card>
  );
};
